import { useEffect, useRef } from "react";

import { CircularProgress, Stack, Typography } from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { useShipmentETAs } from "hooks";
import {
  ISOStringToLocalReadableTimestamp,
  sortRoutePointETAsByRoutePointAndMostRecentlyCreated,
  toFullReadableAddress,
} from "utils";

interface Props {
  shipment: CondensedShipment;
}

export function LatestShipmentRoutePointETADisplay(props: Props) {
  const { shipment } = props;
  const { etas, loading, error, refetchETAs } = useShipmentETAs(
    shipment.info.id,
  );
  const shipmentRef = useRef<undefined | CondensedShipment>();

  useEffect(() => {
    if (
      shipmentRef.current != null &&
      shipmentRef.current.version !== shipment.version
    ) {
      refetchETAs();
    }
    shipmentRef.current = shipment;
  }, [shipment, refetchETAs]);

  if (loading) {
    return (
      <Stack>
        <CircularProgress />
      </Stack>
    );
  }

  if (!etas || etas.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No ETAs created yet.
      </Typography>
    );
  }

  const latestETA =
    sortRoutePointETAsByRoutePointAndMostRecentlyCreated(etas)[0];
  return (
    <LoadingWrappedNode loading={loading}>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          borderColor: "#cecdcd",
          borderWidth: "1px",
          borderStyle: "solid",
          borderRadius: 2,
          p: ".5rem",
        }}
      >
        <Typography variant="body1">
          <b>Latest ETA:</b>
          {` ${ISOStringToLocalReadableTimestamp(latestETA.eta)}`}
        </Typography>
        <Typography variant="body1">
          <b>Route Point:</b>
          {` Point #${latestETA.point.point_number} - ${toFullReadableAddress(latestETA.point.point_location)}`}
        </Typography>
        <Typography variant="body1">
          <b>Notes:</b> {latestETA.notes}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {`ETA created by: ${latestETA.created_by.user.first_name} ${latestETA.created_by.user.last_name} on ${ISOStringToLocalReadableTimestamp(latestETA.created_at)}`}
        </Typography>
        <ErrorTypography error={error} />
      </Stack>
    </LoadingWrappedNode>
  );
}
